import awsExports from "@/aws-exports"
import { GoogleIcon } from "@/components/common/icons/GoogleIcon/GoogleIcon"
import { MicrosoftIcon } from "@/components/common/icons/MicrosoftIcon/MicrosoftIcon"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { Badge } from "@/components/ui/badge"
import { withReferralSource } from "@/features/ariba-onboarding/referralSource"
import {
  getDefaultLanguage,
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { amplitude } from "@/services/analytics/amplitude"
import { BLOCKED_EMAIL_DOMAINS } from "@/utils/blockedEmailDomains"
import {
  getSignInErrorKeyFromError,
  getSignUpErrorKey,
  getSignUpErrorKeyFromError,
  type SignUpErrorKey,
} from "@/utils/cognitoErrors"
import { rememberUpgradeForSignIn } from "@/utils/upgradeEntry"
import { URLS } from "@/utils/urls.const"
import {
  translations as amplifyTranslations,
  Authenticator,
  CheckboxField,
  useAuthenticator,
} from "@aws-amplify/ui-react"
import "@aws-amplify/ui-react/styles.css"
import { AccountInfo } from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"
import {
  confirmSignIn,
  confirmSignUp,
  ConfirmSignUpInput,
  fetchAuthSession,
  resendSignUpCode,
  ResendSignUpCodeInput,
  signIn,
  SignInInput,
  type SignInOutput,
  signInWithRedirect,
  signUp,
  SignUpInput,
} from "aws-amplify/auth"
import { I18n } from "aws-amplify/utils"
import React, { useEffect, useRef } from "react"
import {
  CustomHeader,
  ForceNewPasswordHeader,
  getCustomFormFields,
} from "../CustomSignIn/CustomSignIn"
import "./Authentication.css"
import { sendTokensToExtension } from "./extensionAuthHandoff"
import {
  clearPendingGoogleSignIn,
  listenForGoogleSignInFailure,
  markSignInPending,
  promotePendingSignIn,
  readLastSignInMethod,
  recordSignInMethod,
  type SignInMethod,
} from "./lastSignInMethod"
import {
  completeMicrosoftSignUp,
  resendMicrosoftSignUpCode,
  signUpOrSignInWithMicrosoft,
  takeMicrosoftConfirmSignUpStep,
} from "./microsoftSignUp"
import { withSignUpLanguage } from "./signUpLanguage"
import TagManagerContainer from "./TagManagerContainer"

const loginRequest = {
  scopes: ["User.Read"], // Add the scopes you need
}

// Amplify UI's built-in vocabulary for the <Authenticator> chrome (Sign In,
// Forgot your password?, Reset Password, Cognito error messages, ...).
// Registered once at module scope; language switching happens via
// I18n.setLanguage() below, driven by our own TranslationProvider so the
// very first login screen matches the app's detected/saved language.
I18n.putVocabularies(amplifyTranslations)

// Module scope, not an effect: Amplify handles the OAuth return as soon as it
// is configured (index.tsx), before this component mounts.
listenForGoogleSignInFailure()

const AMPLIFY_I18N_LANGUAGE: Record<string, string> = {
  EN: "en",
  DE: "de",
  JA: "ja",
}

interface AuthQueryParams {
  email: string | undefined
  name: string | undefined
  extension: boolean
  extensionId: string | undefined
  signup: boolean
}

const getQueryParams = (): AuthQueryParams => {
  const params = new URLSearchParams(window.location.search)
  return {
    email: params.get("email") || undefined,
    name: params.get("name") || undefined,
    extension: params.get("extension") === "true",
    extensionId: params.get("extensionId") || undefined,
    signup: params.get("signup") === "true",
  }
}

const readAndClearSignUpRedirectError = (): SignUpErrorKey | null => {
  const params = new URLSearchParams(window.location.search)
  // The hosted UI came back with an error: the Google attempt failed.
  if (params.has("error_description")) {
    clearPendingGoogleSignIn()
  }
  const errorKey = getSignUpErrorKey(params.get("error_description"))

  if (!errorKey) {
    return null
  }

  params.delete("error")
  params.delete("error_description")

  const search = params.toString()
  const nextUrl = `${window.location.pathname}${
    search ? `?${search}` : ""
  }${window.location.hash}`
  window.history.replaceState({}, "", nextUrl)

  return errorKey
}

const normalizeCustomSignUpAttributes = (input: SignUpInput): SignUpInput => {
  const userAttributes = input.options?.userAttributes ?? {}

  return {
    ...input,
    options: {
      ...input.options,
      userAttributes: {
        ...userAttributes,
        "custom:tcAcknowledged":
          userAttributes["custom:tcAcknowledged"] === "yes" ? "true" : "false",
        "custom:newsletterSignup":
          userAttributes["custom:newsletterSignup"] === "yes"
            ? "true"
            : "false",
      },
    },
  }
}

// Function to handle successful authentication for the chrome browser extension
export const handleExtensionAuth = async (
  extensionId: string | undefined,
  { forceRefresh = true }: { forceRefresh?: boolean } = {},
) => {
  try {
    // Force a refresh so the extension never gets a dead session: the extension's
    // logout revokes this same refresh token. If Cognito rejects it, Amplify
    // clears its tokens and the Authenticator returns to the sign-in form.
    // Automatic handoffs the user did not ask for pass forceRefresh: false, so a
    // failed refresh (e.g. throttling) cannot sign them out.
    const session = await fetchAuthSession({ forceRefresh })

    const idToken = session.tokens?.idToken?.toString()
    const accessToken = session.tokens?.accessToken?.toString()

    // since amplify gen2 the refresh token is not return by fetchAuthSession anymore
    // we have to fetch it directly from the local storage then
    const clientId = awsExports.aws_user_pools_web_client_id
    const storageKey = `CognitoIdentityServiceProvider.${clientId}.${session.userSub}.refreshToken`
    const refreshToken = localStorage.getItem(storageKey)

    if (!idToken || !accessToken || !refreshToken) {
      console.error(
        "Extension auth failed: idToken, accessToken, or refreshToken not found",
      )
      return false
    }

    // Create payload for extension
    const authPayload = {
      tokens: {
        idToken: idToken,
        accessToken: accessToken,
        refreshToken: refreshToken,
      },
    }

    const { channel, delivered } = await sendTokensToExtension(
      authPayload,
      extensionId,
    )
    console.debug(
      `EXTENSION_AUTH_CALLBACK via ${channel}: ${delivered ? "delivered" : "not delivered"}`,
    )
    return delivered
  } catch (error) {
    console.error(error)
    return false
  }
}

export const Authentication: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { translate, currentLanguage } = useTranslation()
  const queryParams = getQueryParams()
  const isExtension = queryParams.extension
  const extensionId = queryParams.extensionId
  const [socialLoginErrorKey] = React.useState(readAndClearSignUpRedirectError)
  const socialLoginError = socialLoginErrorKey
    ? translate(socialLoginErrorKey)
    : null
  const { authStatus, user } = useAuthenticator((context) => [
    context.authStatus,
    context.user,
  ])

  // Keep Amplify's own vocabulary (Sign In, Forgot your password?, Cognito
  // error messages, ...) in sync with the app language so the login screen
  // isn't stuck in English regardless of the user's chosen/detected language.
  useEffect(() => {
    I18n.setLanguage(AMPLIFY_I18N_LANGUAGE[currentLanguage] ?? "de")
  }, [currentLanguage])

  // Effect to handle extension auth when user is already signed in
  useEffect(() => {
    if (isExtension && authStatus === "authenticated") {
      console.log("Extension auth triggered by component mount")
      handleExtensionAuth(extensionId)
    }
  }, [isExtension, extensionId, authStatus])

  useEffect(() => {
    if (authStatus === "authenticated") {
      promotePendingSignIn()
    }
  }, [authStatus])

  useEffect(() => {
    if (authStatus === "authenticated" && user && user?.userId) {
      amplitude.setUserId(user.userId)
      amplitude.logEvent("USER_AUTHENTICATED")
    }
  }, [authStatus, user])

  // Pre-warm the session when the tab becomes visible again to avoid stale
  // tokens causing "Error during authentication" toasts after idle periods.
  useEffect(() => {
    if (authStatus !== "authenticated") return

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchAuthSession({ forceRefresh: true }).catch(() => {
          // Silent — if this fails the user will be prompted to log in again
          // by the normal auth flow on the next API call.
        })
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [authStatus])

  return (
    <Authenticator
      formFields={getCustomFormFields(
        translate,
        queryParams.email,
        queryParams.name,
      )}
      initialState={
        queryParams.signup || queryParams.email ? "signUp" : undefined
      }
      components={{
        Header: CustomHeader,
        SignUp: {
          // No GTM on the extension login page: third-party tags must not run
          // next to the token handoff (see extensionAuthHandoff.ts).
          Header: () => (isExtension ? null : <TagManagerContainer />),
          Footer: () => (
            <SocialLoginFooter
              translate={translate}
              errorMessage={socialLoginError}
            />
          ),
          // Reference: https://ui.docs.amplify.aws/react/connected-components/authenticator/customization
          FormFields() {
            const { validationErrors } = useAuthenticator()

            return (
              <>
                {/* Re-use default `Authenticator.SignUp.FormFields` */}
                <Authenticator.SignUp.FormFields />

                {/* Append & require Terms and Conditions field to sign up  */}
                <CheckboxField
                  errorMessage={
                    validationErrors["custom:tcAcknowledged"] as string
                  }
                  hasError={!!validationErrors["custom:tcAcknowledged"]}
                  name="custom:tcAcknowledged"
                  value="yes"
                  label={
                    <span>
                      {translate("SIGNUP/TERMS_LEAD")}{" "}
                      <a
                        href={URLS.INFO_BOX.AGBS}
                        className="text-yellow1 transition-colors hover:text-yellow3"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {translate("LOGIN/TERMS")}
                      </a>{" "}
                      {translate("LOGIN/AND")}{" "}
                      <a
                        href={URLS.INFO_BOX.DATA_POLICY}
                        className="text-yellow1 transition-colors hover:text-yellow3"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {translate("LOGIN/PRIVACY_POLICY")}
                      </a>
                    </span>
                  }
                />
                {/*Append Newsletter field to sign up (optional)*/}
                <CheckboxField
                  name="custom:newsletterSignup"
                  value="yes"
                  label={<span>{translate("SIGNUP/NEWSLETTER")} </span>}
                />
              </>
            )
          },
        },
        SignIn: {
          Footer: () => (
            <SignInFooter
              translate={translate}
              errorMessage={socialLoginError}
            />
          ),
        },
        ForceNewPassword: {
          Header: ForceNewPasswordHeader,
          // Amplify UI v6 preserves `username` in formValues across state transitions
          // (for passwordless flows). However, `username` is a non-writable Cognito
          // attribute and cannot be sent in RespondToAuthChallenge. The
          // handleForceChangePassword actor spreads all non-password form values into
          // userAttributes, so we must clear username before the user submits.
          // `updateForm` is the public typed alias for the internal CHANGE event.
          FormFields() {
            const { updateForm } = useAuthenticator()
            const cleared = useRef(false)

            useEffect(() => {
              if (!cleared.current) {
                cleared.current = true
                updateForm({ name: "username", value: "" })
              }
            }, [updateForm])

            return <Authenticator.ForceNewPassword.FormFields />
          },
        },
      }}
      services={{
        async validateCustomSignUp(formData, touchData) {
          const validationErrors: Record<string, string> = {}

          // Corporate email validation
          if (formData.username) {
            const domain = formData.username.split("@").pop()?.toLowerCase()
            if (domain && BLOCKED_EMAIL_DOMAINS.has(domain)) {
              validationErrors.username = translate(
                "SIGNUP/CORPORATE_EMAIL_REQUIRED",
              )
            }
          }

          // Terms & Conditions validation
          const hasTouchedTerms = touchData["custom:tcAcknowledged"] === true
          const hasSubmittedCoreSignUpFields =
            touchData.username === true &&
            touchData.password === true &&
            touchData.confirm_password === true &&
            touchData.name === true

          if (
            (hasTouchedTerms || hasSubmittedCoreSignUpFields) &&
            formData["custom:tcAcknowledged"] !== "yes"
          ) {
            validationErrors["custom:tcAcknowledged"] =
              translate("SIGNUP/TERMS_ERROR")
          }

          if (Object.keys(validationErrors).length > 0) {
            return validationErrors
          }
        },
        async handleSignIn(input: SignInInput) {
          const customUsername = input.username.toLowerCase()

          const microsoftConfirmStep =
            takeMicrosoftConfirmSignUpStep(customUsername)
          if (microsoftConfirmStep) {
            return microsoftConfirmStep
          }

          let result: SignInOutput
          try {
            result = await signIn({
              username: customUsername,
              password: input.password,
            })
          } catch (error) {
            const errorKey = getSignInErrorKeyFromError(
              error,
              readLastSignInMethod(),
            )
            if (errorKey) {
              throw new Error(translate(errorKey), { cause: error })
            }
            throw error
          }

          if (result.isSignedIn) {
            recordSignInMethod("password")
          } else {
            // Completes inside the Authenticator (new password, MFA, ...).
            markSignInPending("password")
          }

          // Handle extension auth flow after successful sign in
          if (isExtension && result.isSignedIn) {
            console.log("Extension auth triggered by successful sign in")
            await handleExtensionAuth(extensionId)
            console.log("Extension auth completed")
          }

          return result
        },

        async handleSignUp(input: SignUpInput) {
          const normalizedInput = normalizeCustomSignUpAttributes(input)
          const customUsername = normalizedInput.username.toLowerCase()

          try {
            const result = await signUp(
              withReferralSource(
                withSignUpLanguage(
                  { ...normalizedInput, username: customUsername },
                  currentLanguage,
                ),
              ),
            )
            // The auto sign-in after the confirmation skips handleSignIn.
            markSignInPending("password")
            return result
          } catch (error) {
            const errorKey = getSignUpErrorKeyFromError(error)
            if (errorKey) {
              throw new Error(translate(errorKey), { cause: error })
            }
            throw error
          }
        },

        async handleConfirmSignUp(input: ConfirmSignUpInput) {
          const customUsername = input.username.toLowerCase()

          const result = await confirmSignUp(
            withReferralSource({ ...input, username: customUsername }),
          )

          try {
            await completeMicrosoftSignUp(customUsername, result, (idToken) =>
              authenticateWithMicrosoftToken(customUsername, idToken),
            )
          } catch (error) {
            // The account is confirmed; the user can sign in with Microsoft again.
            console.error("Microsoft sign-in after confirmation failed:", error)
          }

          // TODO test auto-signin after sign up
          // For extension sign up flow, attempt to sign in automatically after confirmation
          // if (isExtension && result.isSignUpComplete) {
          //   try {
          //     await signIn({
          //       username: customUsername,
          //       password: input.confirmationCode,
          //     });
          //
          //     // Handle extension auth after successful sign in
          //     await handleExtensionAuth();
          //   } catch (error) {
          //     console.error("Auto sign-in after confirmation failed:", error);
          //   }
          // }

          return result
        },

        async handleResendSignUpCode(input: ResendSignUpCodeInput) {
          return resendMicrosoftSignUpCode(input.username.toLowerCase(), () =>
            resendSignUpCode(input),
          )
        },
      }}
      variation="modal"
    >
      {children}
    </Authenticator>
  )
}

function getRandomCharacter(characters: string): string {
  const randomIndex = Math.floor(Math.random() * characters.length)
  return characters.charAt(randomIndex)
}

function generateSecureRandomString(length: number): string {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
  const numbers = "0123456789"
  const specialCharacters = "!@#$%^&*()-_=+[]{}|;:,.<>?"
  const allCharacters =
    uppercase + numbers + specialCharacters + "abcdefghijklmnopqrstuvwxyz"

  if (length < 4) {
    throw new Error(
      "Length must be at least 4 to include required character types",
    )
  }

  // Ensure at least one character from each required category
  const randomString = [
    getRandomCharacter(uppercase),
    getRandomCharacter(numbers),
    getRandomCharacter(specialCharacters),
  ]

  // Fill the rest of the string
  const remainingLength = length - randomString.length
  const randomValues = new Uint8Array(remainingLength)
  window.crypto.getRandomValues(randomValues)
  for (let i = 0; i < remainingLength; i++) {
    randomString.push(allCharacters[randomValues[i] % allCharacters.length])
  }

  // Shuffle the result to avoid predictable patterns
  for (let i = randomString.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[randomString[i], randomString[j]] = [randomString[j], randomString[i]]
  }

  return randomString.join("")
}

const authenticateWithMicrosoftToken = async (
  username: string,
  msIdToken: string,
) => {
  const cognitoUser = await signIn({
    username,
    options: {
      authFlowType: "CUSTOM_WITHOUT_SRP",
    },
  })
  const { isSignedIn, nextStep } = await confirmSignIn({
    challengeResponse: msIdToken,
  })

  if (isSignedIn) {
    recordSignInMethod("microsoft")
  }
  location.reload()

  return { cognitoUser: cognitoUser, isSignedIn, nextStep }
}

const signUpOrSignInWithMicrosoftToken = async (
  account: AccountInfo,
  msIdToken: string,
) => {
  const attributes: {
    [key: string]: string
  } = {}

  const username = account.username.toLowerCase()
  attributes["email"] = username
  attributes["name"] = account.name ?? username // Fallback to email when no full name is set on the MS account
  attributes["custom:msalIdtoken"] = msIdToken

  const language = getDefaultLanguage()

  return signUpOrSignInWithMicrosoft({
    username,
    idToken: msIdToken,
    signUp: () =>
      signUp(
        withReferralSource(
          withSignUpLanguage(
            {
              username: username,
              password: generateSecureRandomString(30),
              options: { userAttributes: attributes },
            },
            language,
          ),
        ),
      ),
    authenticate: () => authenticateWithMicrosoftToken(username, msIdToken),
  })
}

const isCryptoAvailable = !!window.crypto?.subtle
const MSAL_INTERACTION_IN_PROGRESS = "interaction_in_progress"
const MSAL_USER_CANCELLED = "user_cancelled"

const getErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== "object" || error === null || !("errorCode" in error)) {
    return undefined
  }

  const { errorCode } = error as { errorCode?: unknown }
  return typeof errorCode === "string" ? errorCode : undefined
}

const LAST_USED_BADGE_ID = "last-used-sign-in-method"

// Pill on the provider button the user last signed in with on this device.
const LastUsedBadge: React.FC = () => {
  const { translate } = useTranslation()
  return (
    <Badge
      id={LAST_USED_BADGE_ID}
      className="pointer-events-none absolute -top-2 right-2 px-1.5 py-0 text-[10px] leading-4"
    >
      {translate("LOGIN/LAST_USED")}
    </Badge>
  )
}

const SignInUpWithMicrosoftButton: React.FC<{
  children?: React.ReactNode
  lastUsed?: boolean
}> = ({ children, lastUsed = false }) => {
  const { instance } = useMsal()
  const { translate } = useTranslation()
  const { toSignIn, submitForm } = useAuthenticator((context) => [
    context.route,
  ])
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleLogin = () => {
    if (isLoading) return
    setIsLoading(true)
    setError(null)
    instance
      .loginPopup(loginRequest)
      .then(async (response) => {
        const result = await signUpOrSignInWithMicrosoftToken(
          response.account,
          response.idToken,
        )
        if (result === "CONFIRM_SIGN_UP") {
          // Not auto-confirmed: let the Authenticator open its confirm-signup
          // screen for this user (handleSignIn answers with CONFIRM_SIGN_UP).
          toSignIn()
          submitForm({ username: response.account.username.toLowerCase() })
          return
        }
        location.reload()
      })
      .catch((err) => {
        const errorCode = getErrorCode(err)

        if (errorCode === MSAL_INTERACTION_IN_PROGRESS) {
          console.warn("MSAL interaction already in progress")
          return
        }
        if (errorCode === MSAL_USER_CANCELLED) {
          return
        }

        console.error("Microsoft login failed:", err)
        setError(
          translate(
            getSignUpErrorKeyFromError(err) ?? "ERROR/SOMETHING_WENT_WRONG",
          ),
        )
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <div className="relative flex flex-1 flex-col">
      <button
        type="button"
        onClick={handleLogin}
        disabled={isLoading}
        aria-describedby={lastUsed ? LAST_USED_BADGE_ID : undefined}
        className="amplify-button amplify-field-group__control federated-sign-in-button w-full"
      >
        {isLoading ? (
          <LoadingSpinner size={20} className="mr-2" />
        ) : (
          <MicrosoftIcon className="mr-2 p-0.5" />
        )}
        {children}
      </button>
      {lastUsed && <LastUsedBadge />}
      {error && (
        <p className="mt-1 text-xs text-red1" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

const SocialLoginFooter: React.FC<{
  translate: (key: TranslationKeys) => string
  errorMessage?: string | null
  showLastUsed?: boolean
}> = ({ translate, errorMessage, showLastUsed = false }) => {
  const [lastMethod] = React.useState<SignInMethod | null>(() =>
    showLastUsed ? readLastSignInMethod() : null,
  )
  const googleLastUsed = lastMethod === "google"

  return (
    <div className="social-login-footer">
      {errorMessage && (
        <p className="mb-3 text-xs text-red1" role="alert">
          {errorMessage}
        </p>
      )}
      <div className="social-login-divider">
        <span>{translate("LOGIN/OR")}</span>
      </div>
      <div className="social-login-buttons flex gap-3">
        <div className="relative flex flex-1 flex-col">
          <button
            type="button"
            onClick={() => {
              markSignInPending("google")
              rememberUpgradeForSignIn()
              signInWithRedirect({ provider: "Google" })
            }}
            aria-describedby={googleLastUsed ? LAST_USED_BADGE_ID : undefined}
            className="amplify-button amplify-field-group__control federated-sign-in-button w-full"
          >
            <GoogleIcon className="mr-2 p-0.5" />
            Google
          </button>
          {googleLastUsed && <LastUsedBadge />}
        </div>
        {isCryptoAvailable && (
          <SignInUpWithMicrosoftButton lastUsed={lastMethod === "microsoft"}>
            Microsoft
          </SignInUpWithMicrosoftButton>
        )}
      </div>
    </div>
  )
}

const SignInFooter: React.FC<{
  translate: (key: TranslationKeys) => string
  errorMessage?: string | null
}> = ({ translate, errorMessage }) => {
  return (
    <>
      <Authenticator.SignIn.Footer />
      <SocialLoginFooter
        translate={translate}
        errorMessage={errorMessage}
        showLastUsed
      />
    </>
  )
}
